import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from "@conform-to/zod";

import {
  ErrorList,
  Field,
  GeneralErrorBoundary,
  StatusButton,
} from "@/aptim/aptim";
import {
  json,
  redirect,
  type ActionFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { Form, Link, useActionData } from "@remix-run/react";
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { z } from "zod";
import { SignupEmail } from "~/components/emails/Signup";
import img from "~/img/undraw_join_re_w1lh.svg?url";
import { db } from "~/utils/db.server.ts";
import { sendEmail } from "~/utils/email.server";
import { checkHoneypot } from "~/utils/honeypot.server";
import { appDetails, useIsPending } from "~/utils/misc.tsx";
import { EmailSchema } from "~/utils/user-validation.ts";
import Layout from "./Layout";
import { prepareVerification } from "./verify.server";

const SignupSchema = z.object({
  email: EmailSchema,
});

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();

  checkHoneypot(formData);

  const submission = await parseWithZod(formData, {
    schema: SignupSchema.superRefine(async (data, ctx) => {
      const existingUser = await db.user.findUnique({
        where: { email: data.email.toLowerCase() },
        select: { id: true },
      });
      if (existingUser) {
        ctx.addIssue({
          path: ["email"],
          code: z.ZodIssueCode.custom,
          message: "A user already exists with this email",
        });
        return;
      }
    }),
    async: true,
  });
  if (submission.status !== "success") {
    return json(
      { result: submission.reply() },
      { status: submission.status === "error" ? 400 : 200 }
    );
  }
  const { email } = submission.value;
  const { verifyUrl, redirectTo, otp } = await prepareVerification({
    period: 10 * 60,
    request,
    type: "onboarding",
    target: email.toLowerCase(),
  });

  const response = await sendEmail({
    to: email,
    subject: `Welcome to ${appDetails.title}!`,
    react: <SignupEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
  });

  if (response.status === "success") {
    return redirect(redirectTo.toString());
  } else {
    return json(
      {
        result: submission.reply({ formErrors: [response.error.message] }),
      },
      {
        status: 500,
      }
    );
  }
}

export const meta: MetaFunction = () => {
  return [{ title: `Sign Up | ${appDetails.title}` }];
};

export default function SignupRoute() {
  const actionData = useActionData<typeof action>();
  const isPending = useIsPending();

  const [form, fields] = useForm({
    id: "signup-form",
    constraint: getZodConstraint(SignupSchema),
    lastResult: actionData?.result,
    onValidate({ formData }) {
      const result = parseWithZod(formData, { schema: SignupSchema });
      return result;
    },
    shouldRevalidate: "onBlur",
  });

  return (
    <Layout
      title="Get started with your email address!"
      subtitle={`${appDetails.title} by ${appDetails.companyShortName}`}
      img={img}
    >
      <div className="p-6">
        <Form method="POST" {...getFormProps(form)}>
          <HoneypotInputs />
          <Field
            labelProps={{
              htmlFor: fields.email.id,
              children: "Email",
            }}
            inputProps={{
              ...getInputProps(fields.email, { type: "email" }),
              autoFocus: true,
              autoComplete: "off",
            }}
            errors={fields.email.errors}
            className="mb-4"
          />
          <ErrorList errors={form.errors} id={form.errorId} />
          <StatusButton
            className="w-full"
            status={isPending ? "pending" : form.status ?? "idle"}
            type="submit"
            disabled={isPending}
          >
            Submit
          </StatusButton>
        </Form>
        <div className="flex items-center justify-center gap-2 pt-6">
          <span className="text-muted-foreground">
            Already have an account?
          </span>
          <Link to="/login">Login</Link>
        </div>
        <div className="mt-10">
          <div className="relative">
            <p className="text-muted-foreground">
              Our{" "}
              <a
                href={appDetails.privacyLink}
                target="_blank"
                className="underline"
              >
                privacy notice
              </a>{" "}
              describes how we process your personal data.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
